import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from 'common/api';
import { groupDataByDate } from 'utils/groupDataByDate';

export function useChatList(params: {
  userId: any;
  caseId: any;
  effectiveChatId: string | null;
  onDeletedCurrent: () => void;
  focus?: { type: 'file'; file: string } | { type: 'bundle'; bundle: string } | null;
}) {
  const { userId, caseId, effectiveChatId, onDeletedCurrent, focus } = params;
  const [chatList, setChatList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const didFetchChatListRef = useRef<string | null>(null);
  const focusType = focus?.type;
  const focusBundle = focus && focus.type === 'bundle' ? focus.bundle : undefined;
  const focusFile = focus && focus.type === 'file' ? focus.file : undefined;
  const focusKey = useMemo(() => {
    if (!focus?.type) return '';
    if (focus.type === 'bundle' && focus.bundle) {
      return JSON.stringify({ type: String(focus.type), bundle: String(focus.bundle) });
    }
    if (focus.type === 'file' && focus.file) {
      return JSON.stringify({ type: String(focus.type), file: String(focus.file) });
    }
    return '';
  }, [focusBundle, focusFile, focusType]);

  const fetchChatList = useCallback(
    (options: { clear?: boolean; mode?: 'loading' | 'refreshing' } = {}) => {
      if (!userId || !caseId) return Promise.resolve();
      const { clear = false, mode = 'loading' } = options;

      if (mode === 'loading') {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      if (clear) {
        setChatList([]);
      }

      const apiClient: any = api as any;
      const baseUrl = `/cases/${encodeURIComponent(String(caseId))}/chats/list`;
      const url = focusKey ? `${baseUrl}?focus=${encodeURIComponent(focusKey)}` : baseUrl;
      return Promise.resolve(apiClient.get(url))
        .then((res: any) => {
          if (res?.error) {
            if (clear) setChatList([]);
            return;
          }
          setChatList(Array.isArray(res?.chats) ? res.chats : []);
        })
        .catch(() => {
          if (clear) setChatList([]);
        })
        .finally(() => {
          if (mode === 'loading') {
            setLoading(false);
          } else {
            setRefreshing(false);
          }
        });
    },
    [caseId, focusKey, userId],
  );

  // Fetch chat list once per user+case (guarded for React 18 StrictMode).
  useEffect(() => {
    if (!userId || !caseId) return;
    const key = `${String(userId)}:${String(caseId)}:${focusKey}`;
    if (didFetchChatListRef.current === key) return;
    didFetchChatListRef.current = key;

    fetchChatList({ clear: true, mode: 'loading' });
  }, [userId, caseId, focusKey, fetchChatList]);

  const refreshChatList = useCallback(() => fetchChatList({ mode: 'refreshing' }), [fetchChatList]);

  const groupedChatHistory = useMemo(() => {
    try {
      const items = (chatList || []).map((c: any) => ({
        id: c?.chatId,
        title: c?.chatName || 'Untitled chat',
        createdBy: { date: c?.created || c?.updated },
        raw: c,
      }));
      return groupDataByDate(items);
    } catch {
      return {};
    }
  }, [chatList]);

  const renameChat = useCallback(
    (item: any, newName: string) => {
      const chatId = item.id || item.chatId;
      if (!chatId || !userId) return Promise.reject('Missing chatId or userId');

      const apiClient: any = api as any;
      const url = `/cases/${encodeURIComponent(String(caseId))}/chats/${chatId}/name`;

      return apiClient.put(url, { chatName: newName }).then((res: any) => {
        if (res?.error) throw res.error;

        // Optimistically update the local chat list
        setChatList((prev: any) =>
          prev.map((c: any) =>
            c.chatId === chatId || c.id === chatId ? { ...c, chatName: newName } : c,
          ),
        );
      });
    },
    [caseId, userId],
  );

  const deleteChat = useCallback(
    (item: any) => {
      const chatId = item.id || item.chatId;
      if (!chatId || !userId) return Promise.reject('Missing chatId or userId');

      const apiClient: any = api as any;
      const url = `/cases/${encodeURIComponent(String(caseId))}/chats/${chatId}`;

      return apiClient.del(url).then((res: any) => {
        if (res?.error) throw res.error;

        // Optimistically remove from local chat list
        setChatList((prev: any) => prev.filter((c: any) => c.chatId !== chatId && c.id !== chatId));

        // If we deleted the current chat, reset/navigate away
        if (chatId === effectiveChatId) {
          onDeletedCurrent();
        }
      });
    },
    [caseId, effectiveChatId, onDeletedCurrent, userId],
  );

  return { chatList, loading, refreshing, refreshChatList, groupedChatHistory, renameChat, deleteChat };
}
